import { useCallback } from 'react';
import confetti from 'canvas-confetti';

export type CelebrationType = 'default' | 'fireworks' | 'cannon' | 'stars' | 'emoji';

interface CelebrationOptions {
  type?: CelebrationType;
  duration?: number;
  emoji?: string;
}

/**
 * Hook para celebrações dopaminérgicas no dashboard
 * Uso: const celebrate = useCelebration();
 *      celebrate.fire(); // confetti padrão
 *      celebrate.fireworks(); // fogos de artifício
 *      celebrate.emoji('🎉'); // confetti de emoji
 */
export const useCelebration = () => {
  // Confetti padrão
  const fire = useCallback((options?: CelebrationOptions) => {
    const duration = options?.duration || 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const interval: any = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
      });
    }, 250);
  }, []);

  // Fogos de artifício
  const fireworks = useCallback(() => {
    const duration = 5000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const interval: any = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff'],
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff'],
      });
    }, 250);
  }, []);

  // Canhões laterais
  const cannon = useCallback(() => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { x: 0, y: 0.6 },
      angle: 60,
      zIndex: 9999,
    });
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { x: 1, y: 0.6 },
      angle: 120,
      zIndex: 9999,
    });
  }, []);

  // Chuva de estrelas
  const stars = useCallback(() => {
    const defaults = {
      spread: 360,
      ticks: 100,
      gravity: 0.5,
      decay: 0.94,
      startVelocity: 30,
      shapes: ['star'] as confetti.Shape[],
      colors: ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8'],
      zIndex: 9999,
    };

    confetti({
      ...defaults,
      particleCount: 40,
      scalar: 1.2,
      shapes: ['star'] as confetti.Shape[],
    });

    confetti({
      ...defaults,
      particleCount: 10,
      scalar: 0.75,
      shapes: ['circle'] as confetti.Shape[],
    });
  }, []);

  // Confetti de emoji
  const emoji = useCallback((emojiChar: string = '🎉') => {
    const scalar = 2;
    const emojiUnicode = emojiChar;

    confetti({
      particleCount: 50,
      spread: 100,
      origin: { y: 0.6 },
      zIndex: 9999,
      shapes: [confetti.shapeFromText({ text: emojiUnicode, scalar })],
      scalar,
    });
  }, []);

  // Explosão central
  const burst = useCallback(() => {
    confetti({
      particleCount: 100,
      spread: 160,
      origin: { y: 0.6 },
      colors: ['#26ccff', '#a25afd', '#ff5e7e', '#88ff5a', '#fcff42'],
      zIndex: 9999,
    });
  }, []);

  // Celebração baseada em milestone
  const celebrate = useCallback((milestone: number) => {
    if (milestone === 1) {
      // Primeiro giro/lead - emoji celebração
      emoji('🎉');
    } else if (milestone === 10) {
      // 10 leads - confetti básico
      fire();
    } else if (milestone === 50) {
      // 50 leads - canhões
      cannon();
    } else if (milestone === 100) {
      // 100 leads - fogos
      fireworks();
    } else if (milestone >= 500) {
      // 500+ - estrelas
      stars();
    } else {
      // Padrão
      burst();
    }
  }, [emoji, fire, cannon, fireworks, stars, burst]);

  return {
    fire,
    fireworks,
    cannon,
    stars,
    emoji,
    burst,
    celebrate,
  };
};

function randomInRange(min: number, max: number) {
  return Math.random() * (max - min) + min;
}

export default useCelebration;
